import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { BringzzText, BringzzIcon, BringzzBoxSlider } from "@bringzz/components";
import { useNavigation } from 'context/NavigationContext';
import useRequest from 'hooks/useRequest';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

const Companies = ({ selectedLetter = 'All', setSearchData, callBack, onCompanyProductsClick }) => {
    const { t } = useTranslation();
    const { navigate } = useNavigation();
    const { sendRequest } = useRequest();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [pagination, setPagination] = useState({
        total: 0,
        limit: 20,
        offset: 0
    });
    const observer = useRef();
    const isLoadingMoreRef = useRef(false);

    const fetchCompanies = async (letter = null, offset = 0, append = false) => {
        try {
            if (append) {
                setIsLoadingMore(true);
            } else {
                setLoading(true);
            }
            
            const params = new URLSearchParams({
                limit: 20,
                offset: offset,
                sortOrder: 'ASC'
            });

            if (letter && letter !== 'All' && letter !== '#') {
                params.append('startsWith', letter);
            }

            const response = await sendRequest({
                method: 'GET',
                url: `/module/productdistributor/getAllDistributors?${params.toString()}`
            });

            if (response?.status === 'success') {
                const formattedCompanies = response.data.distributors.map(company => ({
                    id: company.id,
                    label: `${company.legal_name}${company.telephone ? ` * ${company.telephone}` : ''}`,
                    legal_name: company.legal_name,
                    telephone: company.telephone
                }));

                setCompanies(prev => append ? [...prev, ...formattedCompanies] : formattedCompanies);
                setPagination({
                    total: response.data.pagination.total,
                    limit: response.data.pagination.limit,
                    offset: response.data.pagination.offset
                });
            }
        } catch (error) {
            console.error('Failed to fetch companies:', error);
            if (!append) setCompanies([]);
        } finally {
            setLoading(false);
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
        }
    };

    const hasMore = pagination.offset < pagination.total;
    const lastCompanyElementRef = useCallback(node => {
        if (loading || isLoadingMoreRef.current) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !isLoadingMoreRef.current) {
                isLoadingMoreRef.current = true;
                const newOffset = pagination.offset + 20;
                fetchCompanies(selectedLetter, newOffset, true);
            }
        }, {
            threshold: 0.1,
            rootMargin: '100px'
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore, pagination.offset, selectedLetter]);

    useEffect(() => {
        fetchCompanies(selectedLetter, 0);
    }, [selectedLetter]);
    
    const handleCompanyClick = (company) => {
        if (setSearchData) {
            setSearchData({
                method: "company",
                value: company.legal_name,
                results: [company]
            });
        }
        if (callBack) {
            callBack();
        }
    };

    const handleCompanyProductsClick = (company) => {
        if (onCompanyProductsClick) {
            onCompanyProductsClick(company.id, company.legal_name);
        }
    };

    if (loading && companies.length === 0) {
        return (
            <div className='py-2 px-6 mx-2 bg-white rounded-xl'>
                <div className="animate-pulse space-y-3">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="flex items-center h-12 border-b">
                            <div className="w-6 h-6 bg-gray-200 rounded mr-3"></div>
                            <div className="flex-1 h-4 bg-gray-200 rounded"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div>

            {companies.length === 0 && !loading ? (
                <div className="text-center py-8">
                    <BringzzText tag='h3' className="text-gray-500">
                        {t('component.search.general.no_companies_found', { letter: selectedLetter })}
                    </BringzzText>
                </div>
            ) : (
                <>
                    {companies.map((company, index) => (
                        <div
                            key={`${company.id}-${index}`}
                            className={classNames(
                                'h-12 flex justify-between px-6 items-center cursor-pointer hover:bg-gray-50',
                                index !== companies.length - 1 && 'border-b'
                            )}
                            ref={index === companies.length - 1 ? lastCompanyElementRef : null}
                            onClick={() => handleCompanyProductsClick(company)}
                        >
                            <div className='flex space-x-1 items-center'>
                                <BringzzText tag='h4'>{company.legal_name}</BringzzText>
                            </div>
                            <BringzzIcon
                                size="16"
                                icon="IconChevronRight"
                                folder="LineIcons"
                                className="text-gray-400"
                            />
                        </div>
                    ))}

                    {isLoadingMore && (
                        <div className="flex items-center justify-center p-4">
                            <BringzzIcon
                                size="20"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="animate-spin text-magic-lilac"
                            />
                            <span className="ml-2 text-sm text-gray-600">{t('component.search.general.loading_more')}</span>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Companies;